import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SiFormModule } from '@siemens/element-ng/form';
import { SiModalService } from '@siemens/element-ng/modal';
import { STANDARD_MODAL_CONFIG } from 'src/app/main/shared/const';
import { Track, TrackDataSet } from 'src/app/main/shared/DataTypes';
import { AccountService } from 'src/app/main/shared/services/account.service';

import { TrackParameterName, TrackService } from '../../track.service';

/**
 * Manages comments on individual track parameter entries.
 * Provides a modal dialog for viewing, adding, and deleting comments,
 * with special handling for mandatory warning-toggle comments.
 */
@Component({
  selector: 'app-track-comment',
  imports: [TranslateModule, SiFormModule, FormsModule, DatePipe],
  templateUrl: './track-comment.component.html',
  styleUrl: './track-comment.component.scss'
})
export class TrackCommentComponent {
  private readonly trackService = inject(TrackService);
  private readonly modalService = inject(SiModalService);
  protected readonly translateService = inject(TranslateService);
  private readonly accountService = inject(AccountService);
  readonly selectedTrack = model.required<Track | undefined>();
  readonly selectedParameterName = input.required<TrackParameterName>();
  readonly entryIndex = input.required<number>();
  readonly isMandatoryForWarning = input<boolean>(false);
  readonly warningToggleContext = input<{
    entryIndex: number;
    currentWarningState: boolean;
    newWarningState: boolean;
  } | null>(null);

  // Multi-row state management input
  readonly changesSummaryMap = input<Map<string, string>>(new Map());

  readonly commentAdded = output<void>();
  // Emitted when mandatory warning comment modal is closed without adding a comment (user canceled)
  readonly warningCommentCanceled = output<void>();

  readonly newCommentInput = signal<string>('');

  // Template reference for the modal
  readonly modalTemplate = viewChild<TemplateRef<any>>('entryCommentsModalTemplate');
  constructor() {
    effect(() => {
      // Auto-open modal when a warning toggle is pending (both signals must be present)
      if (this.isMandatoryForWarning() && this.warningToggleContext()) {
        const template = this.modalTemplate();
        if (template) {
          // Slight defer to ensure viewChild template is resolved
          setTimeout(() => this.openCommentsModal(template), 0);
        }
      }
    });
    // Removed effect for clearing tracked changes on track change (should be in parent)
  }

  // Helper method to generate row key for multi-row state management
  private getRowKey(): string {
    return `${this.selectedParameterName()}_${this.entryIndex()}`;
  }

  // Computed property to get changes for this specific row
  readonly currentRowChangesSummary = computed(() => {
    const rowKey = this.getRowKey();
    return this.changesSummaryMap().get(rowKey) ?? null;
  });

  // Computed property to get the current entry's comments count
  readonly commentsCount = computed(() => {
    const selectedDataSet = this.selectedTrack()?.parameters?.[this.selectedParameterName()];
    return selectedDataSet?.[this.entryIndex()]?.comments?.length ?? 0;
  });

  // Modal-specific data to maintain context when switching parameters
  readonly modalEntryData = signal<{
    parameterName: TrackParameterName;
    entryIndex: number;
    rowData: TrackDataSet;
  } | null>(null);
  openCommentsModal(template: TemplateRef<any>) {
    // Store the current context for the modal
    const currentParameterName = this.selectedParameterName();
    const selectedDataSet = this.selectedTrack()?.parameters![currentParameterName];
    if (!selectedDataSet) return;

    const rowData = { ...selectedDataSet[this.entryIndex()] };

    this.modalEntryData.set({
      parameterName: currentParameterName,
      entryIndex: this.entryIndex(),
      rowData: rowData
    });

    const modalRef: any = this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      inputValues: {
        entryIndex: this.entryIndex(),
        isMandatory: this.isMandatoryForWarning()
      },
      class: 'responsive-modal modal-dialog-centered'
    });
    // Wrap hide() once to unify backdrop/ESC/user closes
    const originalHide = modalRef.hide?.bind(modalRef);
    modalRef.hide = () => this.finalizeModalClose(originalHide);
  }
  closeModal(modalRef: any): void {
    // Explicit close button -> use same unified path
    const originalHide = modalRef.hide?.bind(modalRef);
    this.finalizeModalClose(originalHide);
  }

  // Centralized close handler: emits cancellation when mandatory warning lacks a comment,
  // clears context, and invokes underlying hide implementation.
  private finalizeModalClose(originalHide?: () => void) {
    if (this.isMandatoryForWarning()) {
      const hasComment = !!this.modalEntryData()?.rowData?.comments?.length;
      if (!hasComment) {
        this.warningCommentCanceled.emit();
      }
    }
    this.newCommentInput.set('');
    this.modalEntryData.set(null);
    originalHide?.();
  }
  getCommentPlaceholder(): string {
    const context = this.warningToggleContext();
    if (context) {
      return context.currentWarningState
        ? this.translateService.instant('TRACK.COMMENTS_MODAL.PLACEHOLDER_WARNING_OFF')
        : this.translateService.instant('TRACK.COMMENTS_MODAL.PLACEHOLDER_WARNING_ON');
    }
    return this.translateService.instant('TRACK.COMMENTS_MODAL.PLACEHOLDER');
  }
  addComment() {
    const trimmed = this.newCommentInput().trim();
    if (trimmed) {
      const modalData = this.modalEntryData();
      if (!modalData) return;

      // Prepare the full comment with warning state change context
      const warningContext = this.warningToggleContext();
      let fullComment: string;
      if (warningContext && this.isMandatoryForWarning()) {
        const warningAction = warningContext.currentWarningState ? 'turned OFF' : 'turned ON';
        // Keep warning comment format as-is
        fullComment = `Warning ${warningAction}: ${trimmed}`;
      } else {
        // Regular comment: show changes first (if any), then the user's comment on a new line
        const currentRowChanges = this.currentRowChangesSummary();
        if (currentRowChanges) {
          fullComment = '\n' + currentRowChanges + '\n\n' + trimmed;
        } else {
          fullComment = trimmed;
        }
      }

      if (this.selectedTrack()!.id) {
        this.trackService
          .addCommentToTrack(
            this.selectedTrack()!.id!,
            modalData.parameterName,
            modalData.entryIndex,
            fullComment
          )
          .subscribe({
            next: (data: any) => {
              this.updateParameterEntryForModal(
                modalData.parameterName,
                modalData.entryIndex,
                entry => ({
                  ...entry,
                  comments: [...data.comments]
                })
              );
              this.newCommentInput.set('');
              this.commentAdded.emit();
            },
            error: (err: unknown) => {
              console.error('Failed to add comment:', err);
            }
          });
      } else {
        // If track is not saved yet, just update the entry locally
        this.updateParameterEntryForModal(modalData.parameterName, modalData.entryIndex, entry => ({
          ...entry,
          comments: [
            ...(entry.comments ?? []),
            {
              content: fullComment,
              date: new Date().toISOString(),
              author:
                this.accountService.accountSummary()!.givenName +
                ' ' +
                this.accountService.accountSummary()!.familyName
            }
          ]
        }));
        this.newCommentInput.set('');
        this.commentAdded.emit();
      }
    }
  }
  deleteComment(commentIndex: number, entryIndex: number) {
    const modalData = this.modalEntryData();
    const parameterName = modalData?.parameterName ?? this.selectedParameterName();

    if (!this.selectedTrack()?.id) {
      this.updateParameterEntryForModal(parameterName, entryIndex, (entry: TrackDataSet) => {
        const updatedComments = [...(entry.comments ?? [])];
        updatedComments.splice(commentIndex, 1);
        return {
          ...entry,
          comments: updatedComments
        };
      });
    }
  }

  formatCommentLines(content: string): string[] {
    if (content == null) return [];
    return String(content).split(/\r?\n/);
  }

  formatDate(date: string | Date): string {
    if (!date) return '';
    const d = new Date(date);
    const lang = this.translateService.getCurrentLang() || undefined;
    const options: Intl.DateTimeFormatOptions =
      this.translateService.getCurrentLang() === 'de'
        ? { day: '2-digit', month: '2-digit', year: 'numeric' }
        : { month: '2-digit', day: '2-digit', year: 'numeric' };
    return d.toLocaleDateString(lang, options);
  }

  private updateParameterEntryForModal(
    parameterName: TrackParameterName,
    entryIndex: number,
    updater: (entry: TrackDataSet) => TrackDataSet
  ) {
    // Update the actual track data
    this.selectedTrack.update(track => {
      if (!track) return track;
      const parameters = { ...track.parameters };
      const paramArray = [...parameters[parameterName]];
      paramArray[entryIndex] = updater(paramArray[entryIndex]);
      parameters[parameterName] = paramArray;
      return { ...track, parameters };
    });

    // Update modal context data to reflect the change
    const modalData = this.modalEntryData();
    if (modalData?.parameterName === parameterName && modalData.entryIndex === entryIndex) {
      this.modalEntryData.set({
        ...modalData,
        rowData: updater(modalData.rowData)
      });
    }
  }
}
